import { Helmet, HelmetProvider } from "react-helmet-async";
import { Tabs, Tab } from "@heroui/react";
import { Sitemap } from "./folder-structure";
import FileLabelManagementTab from "./file-label-tab";
import { CompanyProducts } from "./company-products";
import { useLocation, useNavigate } from "react-router-dom";
import { RenovationFoldersTab } from "./renovation-folders-tab";
import { RenovationLabelsTab } from "./renovation-labels-tab";
import { CompanyFolderStructureTab } from "./company-folder-structure-tab";
import { LedgerAccountsTab } from "./ledger-accounts-tab";
import ProductVariantTemplatesTab from "./product-variant-templates-tab";
import { BuildingAttributeTemplateManager } from "../sites/components/BuildingAttributeTemplateManager";
import MailTemplatesTab from "./mail-templates-tab";
import { RollenbeheerTab } from "./rollenbeheer-tab";
import { VerhuurTab } from "./verhuur-tab";
import EntityFieldConfigPanel from "./components/EntityFieldConfigPanel";
import InsuranceComponentsLibraryTab from "./tabs/insurance-components-library-tab";
import HealthIndexManagementTab from "./health-index-tab";
import CorrectionFactorsTab from "./correction-factors-tab";
import AssetCategoriesTab from "./asset-categories-tab";
import DocumentTemplatesTab from "./document-templates-tab";
import { MetersDocumentenTab } from "./meters-documenten/MetersDocumentenTab";
import DueDiligenceCategoriesTab from "./due-diligence-categories-tab";
import AiUsageTab from "./ai-usage-tab";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";

const OperationalCheckSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [companies, setCompanies] = useState<any>([]);
  const { session } = useSession();

  const getCurrentTab = () => {
    const hash = location.hash.replace("#", "");
    return hash || "varianten";
  };
  const handleTabChange = (key: string | number) => {
    navigate(`#${String(key)}`);
  };

  useEffect(() => {
    // Fetch check templates and categories
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/companies/all`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const companies = await response.json();
      setCompanies(companies);
    } catch (error) {
      console.error("Failed to load Yally companies:", error);
      toast.error("Failed to load Yally companies.");
    }
  };

  // Developer mode error testing functions
  const triggerFrontendError = () => {
    try {
      // Intentionally throw an error for Sentry testing
      throw new Error("[TEST] Frontend error triggered from developer mode");
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Frontend test error sent to Sentry");
    }
  };

  const triggerBackendError = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/debug/test-error`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        toast.success(
          "Backend test error triggered successfully (check Sentry)",
        );
      }
    } catch (error) {
      toast.success("Backend test error triggered successfully (check Sentry)");
    }
  };

  const [odooDebugData, setOdooDebugData] = useState<any>(null);
  const [odooDebugLoading, setOdooDebugLoading] = useState(false);
  const [odooOffset, setOdooOffset] = useState(0);
  const [odooLimit, setOdooLimit] = useState(1);

  // Advanced query builder state
  const [queryFilters, setQueryFilters] = useState<Record<string, string>>({
    date_from: "",
    date_to: "",
    analytic_account_id: "",
    general_account_code: "",
    move_state: "posted",
    amount_min: "",
    amount_max: "",
    partner_name: "",
    journal_name: "",
  });

  const fetchOdooAnalyticLine = async (
    offset: number = odooOffset,
    limit: number = odooLimit,
  ) => {
    setOdooDebugLoading(true);
    try {
      // Build query string with filters
      const params = new URLSearchParams({
        offset: offset.toString(),
        limit: limit.toString(),
      });

      // Add non-empty filters to query
      Object.entries(queryFilters).forEach(([key, value]) => {
        if (value && value.trim() !== "") {
          params.append(key, value.trim());
        }
      });

      const response = await fetchWithAuth(
        `${API_URL}/building-financials/debug/odoo-query?${params.toString()}`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch Odoo data");
      }
      const data = await response.json();
      setOdooDebugData(data);
      setOdooOffset(offset);
      setOdooLimit(limit);
      console.log("🔍 Odoo account.analytic.line FILTERED results:", data);
      console.log("📋 Applied filters:", data.appliedFilters);
      console.log("📋 All field names:", data.allFieldNames);
      console.log("📄 Raw records:", data.records);
      toast.success(`Odoo data geladen - ${data.recordCount} record(s)`);
    } catch (error) {
      console.error("Failed to fetch Odoo analytic line:", error);
      toast.error("Fout bij ophalen Odoo data");
      setOdooDebugData({ error: (error as any)?.message });
    } finally {
      setOdooDebugLoading(false);
    }
  };

  const resetFilters = () => {
    setQueryFilters({
      date_from: "",
      date_to: "",
      analytic_account_id: "",
      general_account_code: "",
      move_state: "posted",
      amount_min: "",
      amount_max: "",
      partner_name: "",
      journal_name: "",
    });
    setOdooDebugData(null);
  };

  const DeveloperModeTab = () => (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border-2 border-yellow-200 bg-yellow-50 p-4">
        <h3 className="mb-2 text-lg font-semibold text-yellow-900">
          ⚠️ Developer Mode
        </h3>
        <p className="mb-4 text-sm text-yellow-700">
          This tab is for testing error tracking in production. Only visible to
          developers.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h4 className="text-md mb-4 font-semibold text-gray-900">
          Sentry Error Testing
        </h4>

        <div className="space-y-4">
          <div className="flex flex-col gap-2">
            <button
              onClick={triggerFrontendError}
              className="rounded-lg bg-red-500 px-4 py-2 text-white transition-colors hover:bg-red-600"
            >
              🔴 Trigger Frontend Error
            </button>
            <p className="text-xs text-gray-500">
              Throws a test error in the React app and sends it to Sentry
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <button
              onClick={triggerBackendError}
              className="rounded-lg bg-orange-500 px-4 py-2 text-white transition-colors hover:bg-orange-600"
            >
              🟠 Trigger Backend Error
            </button>
            <p className="text-xs text-gray-500">
              Calls backend endpoint that throws a test error and sends it to
              Sentry
            </p>
          </div>
        </div>

        <div className="mt-6 border-t border-gray-200 pt-4">
          <p className="text-xs text-gray-400 italic">
            💡 After triggering errors, check your Sentry dashboard to verify
            they were captured correctly.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h4 className="text-md mb-4 font-semibold text-gray-900">
          Odoo Query Builder
        </h4>

        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h5 className="mb-2 text-sm font-semibold text-blue-900">
            📖 Gebruiksinstructies
          </h5>
          <ul className="space-y-1 text-xs text-blue-800">
            <li>
              • <strong>Filters:</strong> Vul alleen de velden in die je wilt
              gebruiken. Lege velden worden genegeerd.
            </li>
            <li>
              • <strong>Datum formaat:</strong> YYYY-MM-DD (bijv. 2025-01-01)
            </li>
            <li>
              • <strong>Analytic Account ID:</strong> Het kostenplaats ID (bijv.
              640 voor een unit)
            </li>
            <li>
              • <strong>Grootboekrekening:</strong> Account code (bijv. 700000,
              400000)
            </li>
            <li>
              • <strong>Move State:</strong> Alleen 'posted' transacties worden
              standaard opgehaald
            </li>
            <li>
              • <strong>Limit:</strong> Maximum 50 records per query voor
              performance
            </li>
          </ul>
        </div>

        <div className="space-y-4">
          {/* Pagination Controls */}
          <div className="flex items-end gap-3 border-b border-gray-200 pb-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Offset (start from):
              </label>
              <input
                type="number"
                value={odooOffset}
                onChange={(e) => setOdooOffset(parseInt(e.target.value) || 0)}
                className="w-24 rounded border border-gray-300 px-3 py-2"
                min="0"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Limit (max 50):
              </label>
              <input
                type="number"
                value={odooLimit}
                onChange={(e) =>
                  setOdooLimit(Math.min(parseInt(e.target.value) || 1, 50))
                }
                className="w-24 rounded border border-gray-300 px-3 py-2"
                min="1"
                max="50"
              />
            </div>
          </div>

          {/* Date Filters */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Datum vanaf:
              </label>
              <input
                type="date"
                value={queryFilters.date_from}
                onChange={(e) =>
                  setQueryFilters({
                    ...queryFilters,
                    date_from: e.target.value,
                  })
                }
                className="rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Datum tot:
              </label>
              <input
                type="date"
                value={queryFilters.date_to}
                onChange={(e) =>
                  setQueryFilters({ ...queryFilters, date_to: e.target.value })
                }
                className="rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Account Filters */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Analytic Account ID (kostenplaats):
              </label>
              <input
                type="text"
                value={queryFilters.analytic_account_id}
                onChange={(e) =>
                  setQueryFilters({
                    ...queryFilters,
                    analytic_account_id: e.target.value,
                  })
                }
                placeholder="bijv. 640"
                className="rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Grootboekrekening code:
              </label>
              <input
                type="text"
                value={queryFilters.general_account_code}
                onChange={(e) =>
                  setQueryFilters({
                    ...queryFilters,
                    general_account_code: e.target.value,
                  })
                }
                placeholder="bijv. 700000"
                className="rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Amount Filters */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Bedrag minimum (€):
              </label>
              <input
                type="number"
                step="0.01"
                value={queryFilters.amount_min}
                onChange={(e) =>
                  setQueryFilters({
                    ...queryFilters,
                    amount_min: e.target.value,
                  })
                }
                placeholder="bijv. 100.00"
                className="rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Bedrag maximum (€):
              </label>
              <input
                type="number"
                step="0.01"
                value={queryFilters.amount_max}
                onChange={(e) =>
                  setQueryFilters({
                    ...queryFilters,
                    amount_max: e.target.value,
                  })
                }
                placeholder="bijv. 1000.00"
                className="rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Other Filters */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Partner naam (bevat):
              </label>
              <input
                type="text"
                value={queryFilters.partner_name}
                onChange={(e) =>
                  setQueryFilters({
                    ...queryFilters,
                    partner_name: e.target.value,
                  })
                }
                placeholder="bijv. Sara"
                className="rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Journal naam (bevat):
              </label>
              <input
                type="text"
                value={queryFilters.journal_name}
                onChange={(e) =>
                  setQueryFilters({
                    ...queryFilters,
                    journal_name: e.target.value,
                  })
                }
                placeholder="bijv. Bank"
                className="rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Move State */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">
              Move State:
            </label>
            <select
              value={queryFilters.move_state}
              onChange={(e) =>
                setQueryFilters({ ...queryFilters, move_state: e.target.value })
              }
              className="w-48 rounded border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Alle states</option>
              <option value="posted">Posted (geboekt)</option>
              <option value="draft">Draft (concept)</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 border-t border-gray-200 pt-4">
            <button
              onClick={() => fetchOdooAnalyticLine(odooOffset, odooLimit)}
              disabled={odooDebugLoading}
              className="rounded-lg bg-blue-500 px-6 py-2 font-medium text-white transition-colors hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {odooDebugLoading ? "⏳ Laden..." : "🔍 Query uitvoeren"}
            </button>
            <button
              onClick={resetFilters}
              className="rounded-lg bg-gray-200 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-300"
            >
              🔄 Reset filters
            </button>
            <button
              onClick={async () => {
                setOdooDebugLoading(true);
                try {
                  const response = await fetchWithAuth(
                    `${API_URL}/building-financials/debug/test-financial-query`,
                    { method: "GET" },
                    session,
                  );

                  if (response.ok) {
                    const data = await response.json();
                    setOdooDebugData(data);
                    toast.success("Test query succesvol uitgevoerd!");
                    console.log("Financial query test results:", data);
                  } else {
                    const errorText = await response.text();
                    toast.error(`Test mislukt: ${response.statusText}`);
                    console.error("Test failed:", errorText);
                  }
                } catch (error) {
                  console.error("Error testing financial query:", error);
                  toast.error("Er is een fout opgetreden bij het testen");
                } finally {
                  setOdooDebugLoading(false);
                }
              }}
              disabled={odooDebugLoading}
              className="rounded-lg bg-green-500 px-4 py-2 font-medium text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {odooDebugLoading ? "⏳ Laden..." : "🧪 Test Financiële Query"}
            </button>
          </div>

          {/* Results Section */}
          {odooDebugData && (
            <div className="mt-4 max-h-[600px] overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h5 className="text-sm font-semibold text-gray-700">
                  Query Resultaten
                </h5>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      JSON.stringify(odooDebugData, null, 2),
                    );
                    toast.success("Gekopieerd naar clipboard");
                  }}
                  className="rounded bg-blue-500 px-3 py-1 text-xs font-medium text-white hover:bg-blue-600"
                >
                  📋 Kopieer JSON
                </button>
              </div>

              {odooDebugData.success ? (
                <div className="space-y-4">
                  {/* Query Summary */}
                  <div className="rounded border border-blue-200 bg-white p-3">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <strong>Records gevonden:</strong>{" "}
                        {odooDebugData.recordCount}
                      </div>
                      <div>
                        <strong>Query tijd:</strong> {odooDebugData.queryTimeMs}
                        ms
                      </div>
                    </div>
                    {odooDebugData.appliedFilters &&
                      Object.keys(odooDebugData.appliedFilters).length > 0 && (
                        <div className="mt-2 border-t border-gray-200 pt-2">
                          <strong className="text-xs text-gray-600">
                            Toegepaste filters:
                          </strong>
                          <pre className="mt-1 text-xs text-gray-700">
                            {JSON.stringify(
                              odooDebugData.appliedFilters,
                              null,
                              2,
                            )}
                          </pre>
                        </div>
                      )}
                  </div>

                  {/* Field Names */}
                  {odooDebugData.allFieldNames && (
                    <div className="rounded border border-gray-300 bg-white p-3">
                      <h6 className="mb-2 text-xs font-semibold text-gray-600">
                        Beschikbare velden ({odooDebugData.allFieldNames.length}
                        ):
                      </h6>
                      <div className="flex flex-wrap gap-1">
                        {odooDebugData.allFieldNames.map((field: string) => (
                          <span
                            key={field}
                            className="rounded bg-gray-100 px-2 py-1 text-xs"
                          >
                            {field}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Records */}
                  {odooDebugData.records &&
                    odooDebugData.records.length > 0 && (
                      <div className="rounded border border-gray-300 bg-white p-3">
                        <h6 className="mb-2 text-xs font-semibold text-gray-600">
                          Records ({odooDebugData.records.length}):
                        </h6>
                        <pre className="overflow-x-auto text-xs">
                          {JSON.stringify(odooDebugData.records, null, 2)}
                        </pre>
                      </div>
                    )}
                </div>
              ) : (
                <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                  <strong>Error:</strong>{" "}
                  {odooDebugData.message || odooDebugData.error}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 border-t border-gray-200 pt-4">
          <p className="text-xs text-gray-400 italic">
            💡 Deze tool haalt ruwe data op van Odoo om de veld structuur te
            inspecteren. Check ook de browser console voor volledige output.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Instellingen &mdash; Yally</title>
        </Helmet>
      </HelmetProvider>
      <div className="z-10 mb-10 flex flex-col rounded-lg bg-white p-4 shadow-md">
        <Tabs
          variant="underlined"
          aria-label="Settings"
          selectedKey={getCurrentTab()}
          onSelectionChange={handleTabChange}
          classNames={{
            base: "shadow-[inset_0_-1px_0_0_rgba(49,64,59,0.2)] mb-6",
            tabList: "p-0 gap-6",
            tab: "text-base px-0 py-0 h-fit",
            tabContent:
              "py-2 w-full text-neutral-secondary-mid group-data-[selected=true]:text-brick-primary",
            cursor: "w-full bg-brick-primary",
          }}
        >
          <Tab
            key="varianten"
            value="companyProducts"
            title="Product varianten"
          >
            <div className="flex flex-col gap-2.5">
              <CompanyProducts companies={companies} />
            </div>
          </Tab>
          <Tab
            key="sharepoint"
            value="folderArchitectuur"
            title="SharePoint structuur"
          >
            <div className="flex flex-col gap-2.5">
              <Sitemap />
            </div>
          </Tab>
          <Tab
            key="renovatie"
            value="renovatieStructuur"
            title="SharePoint structuur renovatie"
          >
            <div className="flex flex-col gap-2.5">
              <RenovationFoldersTab />
            </div>
          </Tab>
          <Tab
            key="renovatieCategorieen"
            value="renovationLabels"
            title="Renovatie categorieën"
          >
            <div className="flex flex-col gap-2.5">
              <RenovationLabelsTab />
            </div>
          </Tab>
          <Tab key="Bestandstypes" value="fileLabels" title="Bestandstypes">
            <div className="flex flex-col gap-2.5">
              <FileLabelManagementTab />
            </div>
          </Tab>
          <Tab
            key="vennootschapsmappen"
            value="companyFolderStructure"
            title="Vennootschapsmappen"
          >
            <div className="flex flex-col gap-2.5">
              <CompanyFolderStructureTab />
            </div>
          </Tab>
          <Tab
            key="attributeTemplates"
            value="attributeTemplates"
            title="Attribuut templates"
          >
            <div className="flex flex-col gap-2.5">
              <BuildingAttributeTemplateManager />
            </div>
          </Tab>
          <Tab
            key="ledgerAccounts"
            value="ledgerAccounts"
            title="Grootboekrekeningen"
          >
            <div className="flex flex-col gap-2.5">
              <LedgerAccountsTab />
            </div>
          </Tab>
          <Tab
            key="productVariantTemplates"
            value="productVariantTemplates"
            title="Productsjablonen"
          >
            <div className="flex flex-col gap-2.5">
              <ProductVariantTemplatesTab />
            </div>
          </Tab>
          <Tab
            key="mailTemplates"
            value="mailTemplates"
            title="E-mail sjablonen"
          >
            <div className="flex flex-col gap-2.5">
              <MailTemplatesTab />
            </div>
          </Tab>
          <Tab key="documentTemplates" value="documentTemplates" title="Document sjablonen">
            <div className="flex flex-col gap-2.5">
              <DocumentTemplatesTab />
            </div>
          </Tab>
          <Tab
            key="emailFields"
            value="emailFields"
            title="Email velden configuratie"
          >
            <div className="flex flex-col gap-2.5">
              <EntityFieldConfigPanel />
            </div>
          </Tab>
          <Tab
            key="insuranceComponents"
            value="insuranceComponents"
            title="Verzekeringscomponenten"
          >
            <div className="flex flex-col gap-2.5">
              <InsuranceComponentsLibraryTab />
            </div>
          </Tab>
          <Tab key="rollenbeheer" value="rollenbeheer" title="Rollenbeheer">
            <div className="flex flex-col gap-2.5">
              <RollenbeheerTab />
            </div>
          </Tab>
          <Tab key="verhuur" value="verhuur" title="Verhuur">
            <div className="flex flex-col gap-2.5">
              <VerhuurTab />
            </div>
          </Tab>
          <Tab
            key="assetCategories"
            value="assetCategories"
            title="Voorzieningencategorieën"
          >
            <div className="flex flex-col gap-2.5">
              <AssetCategoriesTab />
            </div>
          </Tab>
          <Tab
            key="dueDiligence"
            value="dueDiligence"
            title="Due Diligence"
          >
            <div className="flex flex-col gap-2.5">
              <DueDiligenceCategoriesTab />
            </div>
          </Tab>
          <Tab
            key="metersDocumenten"
            value="metersDocumenten"
            title="Meters in documenten"
          >
            <div className="flex flex-col gap-2.5">
              <MetersDocumentenTab />
            </div>
          </Tab>
          <Tab key="healthIndex" value="healthIndex" title="Gezondheidsindex">
            <div className="flex flex-col gap-2.5">
              <HealthIndexManagementTab />
            </div>
          </Tab>
          <Tab
            key="correctionFactors"
            value="correctionFactors"
            title="Correctiefactoren"
          >
            <div className="flex flex-col gap-2.5">
              <CorrectionFactorsTab />
            </div>
          </Tab>
          <Tab key="aiUsage" value="aiUsage" title="AI Verbruik">
            <div className="flex flex-col gap-2.5">
              <AiUsageTab />
            </div>
          </Tab>
          <Tab key="sys" value="sys" title="Systeem">
            <div className="flex flex-col gap-2.5">
              <DeveloperModeTab />
            </div>
          </Tab>
        </Tabs>
      </div>
    </>
  );
};

export default OperationalCheckSettings;
